import { LuminaireAnalytics, RadarAxisDef } from '../types/analytics';

/**
 * Radar axis normalization rules.
 *
 * SCIENTIFIC NOTE FOR REVIEWERS:
 * The radar polygon is only visually interpretable if every axis follows
 * the same "higher = better" convention. Each rule documents:
 *   - The raw value it consumes
 *   - The mapping to the 0..100 radar scale
 *   - Whether the raw value is inverted before mapping
 *
 * A luminaire with all-100s on every axis must produce a perfect octagon —
 * this is the sanity check for the normalization layer.
 */

const clamp0To100 = (v: number) => Math.max(0, Math.min(100, v));

export const RADAR_AXES: RadarAxisDef[] = [
  {
    key: 'compatibility',
    label: 'Compatibility Score',
    description:
      'Mean match score vs. TAMAN-generated optimal distributions across all projects in the selected period. Already on a 0–100 scale.',
    unit: '%',
    inverted: false,
    normalize: (a) => clamp0To100(a.compatibilityScoreAvg),
    rawValue: (a) => a.compatibilityScoreAvg,
    format: (v) => `${v.toFixed(1)}%`,
  },
  {
    key: 'matchFrequency',
    label: 'Match Frequency',
    description:
      'How often this luminaire appears in Market Match top-N results per month. Log-scaled because the distribution has a long tail — caps around 1000/mo as full-scale.',
    unit: '/mo',
    inverted: false,
    normalize: (a) => clamp0To100(Math.log10(a.matchFrequencyPerMonth + 1) * 33.3),
    rawValue: (a) => a.matchFrequencyPerMonth,
    format: (v) => `${Math.round(v)} / mo`,
  },
  {
    key: 'applyRate',
    label: 'Apply Rate',
    description:
      'Of the times this luminaire was shown in Market Match, the share where the planner clicked "Apply". Already a 0–100 percentage.',
    unit: '%',
    inverted: false,
    normalize: (a) => clamp0To100(a.applyRatePercent),
    rawValue: (a) => a.applyRatePercent,
    format: (v) => `${v.toFixed(1)}%`,
  },
  {
    key: 'favoriteRate',
    label: 'Favorite Rate',
    description:
      'Share of Market Match appearances that resulted in the planner favoriting this luminaire. Already a 0–100 percentage.',
    unit: '%',
    inverted: false,
    normalize: (a) => clamp0To100(a.favoriteRatePercent),
    rawValue: (a) => a.favoriteRatePercent,
    format: (v) => `${v.toFixed(1)}%`,
  },
  {
    key: 'projectCoverage',
    label: 'Project Coverage',
    description:
      "How many of TAMAN's 10 project types this luminaire has been matched into. Mapped linearly: 10 / 10 → 100.",
    unit: ' types',
    inverted: false,
    normalize: (a) => clamp0To100((a.projectTypeCount / 10) * 100),
    rawValue: (a) => a.projectTypeCount,
    format: (v) => `${Math.round(v)} / 10`,
  },
  {
    key: 'leadConversion',
    label: 'Lead Conversion',
    description:
      'Share of Market Match appearances that resulted in a contact/quote inquiry being sent to the manufacturer. Already a 0–100 percentage.',
    unit: '%',
    inverted: false,
    normalize: (a) => clamp0To100(a.leadConversionRatePercent),
    rawValue: (a) => a.leadConversionRatePercent,
    format: (v) => `${v.toFixed(1)}%`,
  },
  {
    key: 'optimalGap',
    label: 'Optimal Gap',
    description:
      'Mean deviation from the TAMAN optimal distribution. Raw value: lower is better. INVERTED for the radar: displayed as 100 − gap, so a fuller polygon means closer to optimal.',
    unit: '%',
    inverted: true,
    normalize: (a) => clamp0To100(100 - a.optimalGapPercent),
    rawValue: (a) => a.optimalGapPercent,
    format: (v) => `${v.toFixed(1)}%`,
  },
  {
    key: 'regionalSpread',
    label: 'Regional Spread',
    description:
      'Count of distinct countries (ISO codes) where this luminaire has been matched. Capped at 20 regions = full scale.',
    unit: ' regions',
    inverted: false,
    normalize: (a) => clamp0To100((a.regionsCount / 20) * 100),
    rawValue: (a) => a.regionsCount,
    format: (v) => `${Math.round(v)}`,
  },
];

/** Synthetic analytics object that yields 100 on every axis — the "optimal" anchor. */
export const OPTIMAL_ANALYTICS: LuminaireAnalytics = {
  compatibilityScoreAvg: 100,
  compatibilityScoreP90: 100,
  optimalGapPercent: 0,
  matchFrequencyTotal: 12000,
  matchFrequencyPerMonth: 1000,
  top3MatchCount: 4000,
  top100MatchCount: 36000,
  applyCount: 4000,
  applyRatePercent: 100,
  favoriteCount: 2000,
  favoriteRatePercent: 100,
  projectTypesCovered: [],
  projectTypeCount: 10,
  regionsActive: [],
  regionsCount: 20,
  inquiriesGenerated: 1000,
  leadConversionRatePercent: 100,
  monthlyTimeSeries: [],
  lastMatchedAt: null,
  firstSeenAt: '',
};
